import React, { useEffect, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';

interface Props {
  src?: string | null;
  alt?: string;
  category?: string;
  sx?: SxProps;
  placeholderSx?: SxProps;
  iconSize?: number;
  fallback?: React.ReactNode;
  className?: string;
}

/**
 * Curated category → Unsplash food photo. Used as a graceful second
 * fallback when the row's own `image_url` returns 403/404 (e.g. broken
 * SushiKi-era links). Adds `&w=400&auto=format&q=70` for sane sizing.
 */
const CATEGORY_IMAGES: Record<string, string> = {
  // Sushi family
  'crispy rolls':           'https://images.unsplash.com/photo-1617196034796-73dfa7b1fd56',
  'inside out':             'https://images.unsplash.com/photo-1617196701537-7329482cc9fe',
  'inside-out special':     'https://images.unsplash.com/photo-1611143669185-af224c5e3252',
  'maki':                   'https://images.unsplash.com/photo-1611143669185-af224c5e3252',
  'nigiri':                 'https://images.unsplash.com/photo-1564489563601-c53cfc451e93',
  'sashimi':                'https://images.unsplash.com/photo-1534482421-64566f976cfa',
  'poke bowls':             'https://images.unsplash.com/photo-1546069901-ba9599a7e63c',
  'sushi box':              'https://images.unsplash.com/photo-1583623025817-d180a2221d0a',
  'sushi':                  'https://images.unsplash.com/photo-1579871494447-9811cf80d66c',
  // Hot & savoury
  'burgers':                'https://images.unsplash.com/photo-1568901346375-23c9450c58cd',
  'pizza':                  'https://images.unsplash.com/photo-1574071318508-1cdbab80d002',
  'pizza star':             'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
  'pizza starter':          'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
  'pasta':                  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601',
  'shawarma':               'https://images.unsplash.com/photo-1561651823-34feb02250e4',
  'soups':                  'https://images.unsplash.com/photo-1547592180-85f173990554',
  'starters':               'https://images.unsplash.com/photo-1541544741938-0af808871cc0',
  'salads':                 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd',
  // Sweet & drinks
  'desserts':               'https://images.unsplash.com/photo-1551024506-0bccd828d307',
  'desserts & daifuku mochis': 'https://images.unsplash.com/photo-1607478900766-efe13248b125',
  'beverages':              'https://images.unsplash.com/photo-1544145945-f90425340c7e',
  'drinks':                 'https://images.unsplash.com/photo-1544145945-f90425340c7e',
  'extras':                 'https://images.unsplash.com/photo-1565958011703-44f9829ba187',
  'extra dips':             'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136',
};

const DEFAULT_FALLBACK = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836';

function categoryImage(category?: string): string {
  if (!category) return `${DEFAULT_FALLBACK}?w=400&auto=format&q=70`;
  const key = category.toLowerCase().trim();
  const url = CATEGORY_IMAGES[key] || DEFAULT_FALLBACK;
  return `${url}?w=400&auto=format&q=70`;
}

/**
 * Menu image with graceful fallback chain:
 *   1. row's own `image_url`
 *   2. category-based stock photo (Unsplash)
 *   3. icon placeholder
 */
export const MenuImage: React.FC<Props> = ({ src, alt, category, sx, placeholderSx, iconSize = 36, fallback, className }) => {
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  useEffect(() => { setStage(0); }, [src, category]);

  const primaryOk = !!src && stage === 0;
  const fallbackOk = stage <= 1;
  const showPlaceholder = stage === 2 || (!src && !category);

  return (
    <Box className={className} sx={{ width: '100%', height: '100%', position: 'relative', ...sx }}>
      {primaryOk ? (
        <Box
          component="img"
          src={src as string}
          alt={alt || ''}
          loading="lazy"
          onError={() => setStage(1)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : !showPlaceholder && fallbackOk && category ? (
        <Box
          component="img"
          src={categoryImage(category)}
          alt={alt || category || ''}
          loading="lazy"
          onError={() => setStage(2)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : fallback ? (
        fallback
      ) : (
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94a3b8',
          backgroundColor: '#1a1d2e',
          ...placeholderSx,
        }}>
          <ImageIcon sx={{ fontSize: iconSize }} />
        </Box>
      )}
    </Box>
  );
};

export default MenuImage;
